import { CORE_DEPARTMENTS } from '../pages/core/coverSheet'
import { filterDynamicTableRows, resolveDynamicTableRowHeaderOptions } from '../../components/form/DynamicTable'
import { getLockedSubsections, isSubsectionNotRequired } from '../../utils/config-utils'
import formConfig from './gfie'

describe('GFIE form config', () => {
    it('provides submitter details followed by departmental and cross-government returns', () => {
        expect(formConfig.live).toBe(true)
        expect(formConfig.limitWidth).toBeUndefined()
        expect(formConfig.approval).toBeUndefined()
        expect(formConfig.sections.map(section => section.title)).toEqual(['GFiE Pathway data Return'])

        const subsections = formConfig.sections[0].subsections
        expect(subsections.map(subsection => subsection.slug)).toEqual([
            'submitter-details',
            'pathway-data-return',
            'cross-government-pathway-return'
        ])
        expect(subsections[1]).toEqual(
            expect.objectContaining({
                title: 'Pathway data return',
                dependsOn: 'submitter-details'
            })
        )
        expect(subsections[1]).not.toHaveProperty('notRequiredWhen')
        expect(subsections[2]).toEqual(
            expect.objectContaining({
                title: 'Cross Government Pathway',
                dependsOn: 'submitter-details',
                notRequiredWhen: [expect.objectContaining({ condition: 'selectedOptionsNoneEqual', value: 'Yes' })]
            })
        )

        const annexAPages = subsections.flatMap(subsection => subsection.pages)
        expect(annexAPages.map(page => page.name)).toEqual([
            'submitter-details',
            'gfie-pathway-data',
            'gfie-cross-government-pathway-information'
        ])
        expect(annexAPages.some(page => page.pageType === 'information')).toBe(false)

        const submitterPage = annexAPages[0]
        expect(submitterPage).toEqual(
            expect.objectContaining({
                title: '1.1 Submitter details',
                description:
                    'Many thanks for completing this return. By sharing this information you are helping us to get a picture of activity across all accredited Pathways.'
            })
        )
        const submitterQuestions = submitterPage.pageType === 'question' ? (submitterPage.questions ?? []) : []
        expect(submitterQuestions).toHaveLength(4)
        expect(submitterQuestions.find(question => question.name === 'report-period')).toEqual(
            expect.objectContaining({
                questionType: 'reportPeriod',
                defaultValue: 'auto',
                rules: { required: true }
            })
        )
        expect(submitterQuestions.find(question => question.name === 'gfie-contact-email')).toEqual(
            expect.objectContaining({
                questionType: 'textInput',
                userEmail: true,
                rules: { required: true, email: true }
            })
        )
        expect(submitterQuestions.find(question => question.name === 'gfie-department')).toEqual(
            expect.objectContaining({
                questionType: 'select',
                options: CORE_DEPARTMENTS,
                rules: { required: true }
            })
        )
        const pathways = submitterQuestions.find(question => question.name === 'gfie-pathways')
        expect(pathways).toEqual(
            expect.objectContaining({
                questionType: 'checkbox',
                title: 'Please select the pathways you wish to report upon',
                hint: "Please provide data for the Pathway you are accredited to run only to avoid duplicate counting i.e. HMRC using Cabinet Office GFiE Prison Leavers will be counted by the Cabinet Office team and should not feature in HMRC's return.",
                rules: { required: true }
            })
        )
        const pathwayOptions = Array.isArray(pathways?.options) ? pathways.options : []
        const conditionalPathwayOptions = pathwayOptions.filter(
            (option): option is Exclude<(typeof pathwayOptions)[number], string> => typeof option !== 'string'
        )
        expect(conditionalPathwayOptions).toHaveLength(20)
        const pathwayLabels = conditionalPathwayOptions.map(option =>
            'option' in option ? option.option : option.label
        )
        expect(pathwayLabels).toEqual([
            'Cabinet Office - Pathway for Care leavers',
            'Cabinet Office - Pathway for Carers',
            'Cabinet Office - Pathway for Military partners/spouses',
            'Cabinet Office - Pathway for Veterans',
            'Cabinet Office Pathway for Autistic People',
            'Cabinet Office Pathway for People with Convictions',
            'CPS Diversifying CPS Careers',
            'DCMS Learn & Grow Pathway',
            'DFE Care Leavers internship',
            'DFE Supported Internships',
            'DHSC Workfit',
            'DWP Movement to Work',
            'DWP Social Mobility Apprenticeship',
            'Home Office Moving forward',
            'Home Office Pathway to Employment',
            'IPO - Pathways to STEM Programme',
            'MHCLG Civil Service Masterclass',
            'MOJ Probation Employment Pathway',
            'Scottish Government Employability in Scotland',
            'Stepping into Communications (GCS Internship Scheme)'
        ])
        const optionDefaults = Object.fromEntries(
            conditionalPathwayOptions.map(option => [
                'option' in option ? option.option : option.label,
                'questions' in option ? option.questions?.[0]?.defaultValue : undefined
            ])
        )
        expect(optionDefaults['CPS Diversifying CPS Careers']).toBe('Yes')
        expect(optionDefaults['DCMS Learn & Grow Pathway']).toBe('No')

        const dataPage = annexAPages[1]
        expect(dataPage).toEqual(
            expect.objectContaining({
                title: 'Pathway data return',
                limitWidth: false
            })
        )
        expect(dataPage.disabled).toBeUndefined()
        const dataQuestions = dataPage.pageType === 'question' ? (dataPage.questions ?? []) : []
        expect(dataQuestions).toHaveLength(1)
        const table = dataQuestions[0]
        expect(table).toEqual(
            expect.objectContaining({
                questionType: 'dynamicTable',
                title: 'GFiE pathway data',
                hint: 'Please provide data for the period {report-period.dateRange} ({report-period.quarterNumber})',
                rules: { required: true }
            })
        )
        expect(table.dynamicTable).toEqual(
            expect.objectContaining({
                layout: 'grouped',
                rowFilter: expect.objectContaining({
                    field: 'gfie-pathways',
                    match: 'label'
                })
            })
        )
        expect(table.dynamicTable?.rowFilter).not.toHaveProperty('conditionalAnswers')
        expect(table.dynamicTable?.columns).toHaveLength(23)
        expect(table.dynamicTable?.columns.every(column => column.minWidth === undefined)).toBe(true)
        expect(table.dynamicTable?.columns.filter(column => column.inputType === 'text')).toEqual([
            expect.objectContaining({
                key: 'comments',
                label: 'Any comments about data or extra info',
                headerLineLimit: 1,
                required: false
            })
        ])
        expect(table.dynamicTable?.columns.every(column => column.allowNegativeNumbers !== true)).toBe(true)
        expect(table.dynamicTable?.columns.find(column => column.key === 'averageTimeToHire')?.allowDecimal).toBe(true)
        expect(table.dynamicTable?.rows).toHaveLength(21)
        expect(table.dynamicTable?.groups?.map(group => group.label)).toEqual([
            'Recruitment & PEC Data',
            'Time to Hire Data',
            'Location Data'
        ])
        expect(table.dynamicTable?.rows[20]).toEqual(
            expect.objectContaining({ label: 'Total', calculation: 'columnSum' })
        )
        expect(table.dynamicTable?.columns.find(column => column.key === 'locationTotal')).toEqual(
            expect.objectContaining({ calculation: 'rowSum', sourceColumns: expect.any(Array) })
        )
        expect(table.dynamicTable?.columns.at(-1)?.key).toBe('comments')
        expect(table.dynamicTable?.groups?.find(group => group.key === 'location')?.columnKeys.at(-1)).toBe('comments')
        expect(
            dataQuestions.some(question =>
                [
                    'gfie-annex-a-recruitment-and-pec-data',
                    'gfie-annex-a-time-to-hire-data',
                    'gfie-annex-a-location-data'
                ].includes(question.name)
            )
        ).toBe(false)

        const crossGovernmentPage = annexAPages[2]
        expect(crossGovernmentPage).toEqual(
            expect.objectContaining({
                title: '1.3 Cross Government Pathway',
                limitWidth: false
            })
        )
        const crossGovernmentQuestions =
            crossGovernmentPage.pageType === 'question' ? (crossGovernmentPage.questions ?? []) : []
        const crossGovernmentTable = crossGovernmentQuestions[0]
        expect(crossGovernmentTable).toEqual(
            expect.objectContaining({
                questionType: 'dynamicTable',
                title: 'Cross Government Pathway Information',
                rules: { required: true }
            })
        )
        expect(crossGovernmentTable.dynamicTable).toEqual(
            expect.objectContaining({
                continuous: true,
                initialContinuousEditableRowCount: 0,
                sortOnSaveBy: 'rowHeader',
                addRowBackground: 'lightBlue',
                rowHeaderInput: expect.objectContaining({
                    inputType: 'select',
                    prepopulateOptions: true,
                    optionsFrom: expect.objectContaining({ answerValue: 'Yes' })
                })
            })
        )
    })

    it('shows every selected pathway in the data return and only Yes pathways in the cross-government return', () => {
        const subsections = formConfig.sections[0].subsections
        const departmentalReturn = subsections[1]
        const crossGovernmentReturn = subsections[2]
        const pages = subsections.flatMap(subsection => subsection.pages)
        const departmentalPage = pages.find(page => page.name === 'gfie-pathway-data')
        const crossGovernmentPage = pages.find(page => page.name === 'gfie-cross-government-pathway-information')
        const departmentalTable =
            departmentalPage?.pageType === 'question' ? departmentalPage.questions?.[0]?.dynamicTable : undefined
        const crossGovernmentTable =
            crossGovernmentPage?.pageType === 'question' ? crossGovernmentPage.questions?.[0]?.dynamicTable : undefined
        const yesLabel = 'CPS Diversifying CPS Careers'
        const noLabel = 'DCMS Learn & Grow Pathway'
        const yesField = 'gfie-pathway-cross-government-cpsDiversyfingCareers'
        const noField = 'gfie-pathway-cross-government-dcmsLearnAndGrowPathway'
        const mixedAnswers = {
            'gfie-pathways': [yesLabel, noLabel],
            [yesField]: 'Yes',
            [noField]: 'No'
        }

        expect(filterDynamicTableRows(departmentalTable!, mixedAnswers).rows.map(row => row.label)).toEqual([
            yesLabel,
            noLabel,
            'Total'
        ])
        expect(
            resolveDynamicTableRowHeaderOptions(crossGovernmentTable!, mixedAnswers).rowHeaderInput?.options
        ).toEqual([yesLabel])
        expect(isSubsectionNotRequired(departmentalReturn, { answers: mixedAnswers })).toBe(false)
        expect(isSubsectionNotRequired(crossGovernmentReturn, { answers: mixedAnswers })).toBe(false)

        const onlyYes = { 'gfie-pathways': [yesLabel], [yesField]: 'Yes' }
        expect(isSubsectionNotRequired(departmentalReturn, { answers: onlyYes })).toBe(false)
        expect(isSubsectionNotRequired(crossGovernmentReturn, { answers: onlyYes })).toBe(false)

        const onlyNo = { 'gfie-pathways': [noLabel], [noField]: 'No' }
        expect(isSubsectionNotRequired(departmentalReturn, { answers: onlyNo })).toBe(false)
        expect(isSubsectionNotRequired(crossGovernmentReturn, { answers: onlyNo })).toBe(true)
    })

    it('locks both returns until every submitter detail is saved', () => {
        const subsectionMap = Object.fromEntries(
            formConfig.sections.flatMap(section => section.subsections.map(subsection => [subsection.slug, subsection]))
        )
        const getLocks = (answers: Record<string, unknown>) =>
            getLockedSubsections(formConfig.sections, subsectionMap, { answers })

        expect(getLocks({})['pathway-data-return']).toBe(true)
        expect(getLocks({})['cross-government-pathway-return']).toBe(true)

        const completedSubmitterDetails = {
            'report-period': JSON.stringify({
                quarter: 'Quarter 4 (January-March)',
                year: '2025-26'
            }),
            'gfie-contact-email': 'submitter@example.gov.uk',
            'gfie-department': 'Cabinet Office',
            'gfie-pathways': ['CPS Diversifying CPS Careers'],
            'gfie-pathway-cross-government-cpsDiversyfingCareers': 'Yes'
        }

        expect(getLocks(completedSubmitterDetails)['pathway-data-return']).toBe(false)
        expect(getLocks(completedSubmitterDetails)['cross-government-pathway-return']).toBe(false)
    })
})
